/*
 * Ivatherm campaign system — <iva-campaign-timer>
 * Drives the native .countdown-timer__* markup (flip-clock look) for the campaign
 * teaser (A1) and, later, the campaign page (A2/A3).
 *
 * Why a custom element instead of Grasse's native <countdown-timer>:
 *  - the native one fires NO event at zero (we need the teaser→campaign redirect)
 *  - it uses a fixed GMT offset that breaks across the DST boundary
 *
 * DST-safe by construction: the section emits a wall-clock ISO string + the shop's
 * numeric offset (e.g. "+0300"); we assemble a strict ISO ("...+03:00") and let
 * `new Date(iso).getTime()` resolve it to a UTC instant. The live/expired decision
 * is made here in JS against Date.now(), so it stays correct even on a cached page.
 *
 * Attributes:
 *   data-mode="teaser"        countdown to target → redirect at zero
 *   data-mode="campaign"      countdown to target → "ended" state at zero (no redirect)
 *   data-target="2026-06-20T09:00:00"   wall-clock ISO (no offset)
 *   data-tz="+0300"           shop offset for that instant (may be omitted/Z/with colon)
 *   data-redirect="/pages/black-friday"  teaser redirect target
 */
(function () {
  if (window.customElements && customElements.get('iva-campaign-timer')) return;

  function toIso(wall, tz) {
    if (!wall) return null;
    // already has an explicit offset or Z → use as-is
    if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(wall)) {
      return wall.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    }
    if (!tz) return wall; // no offset info → let the browser parse as local (test fallback)
    var t = tz.trim();
    if (t === 'Z') return wall + 'Z';
    // normalise "+0300" → "+03:00"
    t = t.replace(/^([+-]\d{2})(\d{2})$/, '$1:$2');
    return wall + t;
  }

  var UNITS = ['days', 'hours', 'minutes', 'seconds'];

  class IvaCampaignTimer extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'teaser';
      this.redirect = this.dataset.redirect || '';
      var iso = toIso(this.dataset.target, this.dataset.tz);
      this.targetMs = iso ? new Date(iso).getTime() : NaN;

      this.numEls = {};
      UNITS.forEach(function (u) {
        var el = this.querySelector('.iva-ct__num[data-unit="' + u + '"]');
        if (el) this.numEls[u] = el;
      }, this);

      if (isNaN(this.targetMs)) return; // no/invalid date → render the static "00"s, do nothing

      this.tick();
      this.interval = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    setNum(unit, value) {
      var el = this.numEls[unit];
      if (!el) return;
      var str = String(value).padStart(2, '0');
      if (el.dataset.val === str) return; // only re-render + animate on change
      el.dataset.val = str;
      // render each digit via safe DOM methods (computed numerals, no innerHTML/XSS)
      el.textContent = '';
      for (var i = 0; i < str.length; i++) {
        var span = document.createElement('span');
        span.className = 'iva-ct__digit';
        span.textContent = str[i];
        el.appendChild(span);
      }
      // soft fade/slide on change (CSS gates it behind prefers-reduced-motion)
      el.classList.remove('iva-ct__tick');
      void el.offsetWidth; // reflow to retrigger the animation
      el.classList.add('iva-ct__tick');
    }

    render(diffMs) {
      var totalSec = Math.max(0, Math.floor(diffMs / 1000));
      var d = Math.floor(totalSec / 86400);
      var h = Math.floor((totalSec % 86400) / 3600);
      var m = Math.floor((totalSec % 3600) / 60);
      var s = totalSec % 60;
      this.setNum('days', d);
      this.setNum('hours', h);
      this.setNum('minutes', m);
      this.setNum('seconds', s);
    }

    tick() {
      var diff = this.targetMs - Date.now();
      if (diff <= 0) {
        this.render(0);
        this.onReached();
        return;
      }
      this.render(diff);
    }

    onReached() {
      if (this.interval) clearInterval(this.interval);
      this.classList.add('is-ended');
      // never redirect inside the Theme Editor — it would bounce the merchant out
      if (window.Shopify && window.Shopify.designMode) return;
      // When the countdown reaches zero, redirect if a target is set: the teaser
      // forwards to the campaign page at start (data-redirect). The campaign page has
      // no target, so it just shows the ended state. replace() keeps the redirected-
      // from page out of the back-button history.
      if (this.redirect) window.location.replace(this.redirect);
    }
  }

  customElements.define('iva-campaign-timer', IvaCampaignTimer);
})();

/*
 * <iva-campaign-catalog> — campaign page (A3) controllers:
 *  - category pills = FILTERS: click shows only that category (or "Toate" = all);
 *    single render() combines the active filter + the search query
 *  - on-page search: client-side filter over rendered cards (no predictive search)
 *  - sticky pills get a frosted backdrop only when stuck to the top (IntersectionObserver)
 * Editor-safe: custom element upgrades on shopify:section:load automatically.
 */
(function () {
  if (window.customElements && customElements.get('iva-campaign-catalog')) return;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function toArr(nodeList) { return Array.prototype.slice.call(nodeList); }

  class IvaCampaignCatalog extends HTMLElement {
    connectedCallback() {
      this.pills = toArr(this.querySelectorAll('[data-iva-filter]'));
      this.cats = toArr(this.querySelectorAll('[data-iva-cat]'));
      this.activeFilter = 'all';
      this.query = '';
      this.initFilters();
      this.initSearch();
      this.initStickyBar();
    }

    disconnectedCallback() {
      if (this.stuckIo) this.stuckIo.disconnect();
      if (this._stuckHandler) {
        window.removeEventListener('scroll', this._stuckHandler);
        window.removeEventListener('resize', this._stuckHandler);
      }
    }

    // Single source of truth: show a category only if it's in the active filter
    // AND (no search OR it has matching products). Combines filter + search cleanly.
    render() {
      var self = this;
      this.cats.forEach(function (cat) {
        var inFilter = self.activeFilter === 'all' || cat.getAttribute('data-iva-filter-id') === self.activeFilter;
        var visibleCards = 0;
        toArr(cat.querySelectorAll('[data-iva-card]')).forEach(function (card) {
          var hit = !self.query || (card.getAttribute('data-search') || '').indexOf(self.query) !== -1;
          card.hidden = !hit;
          if (hit) visibleCards++;
        });
        var empty = cat.querySelector('[data-iva-empty]');
        if (empty) empty.hidden = !(inFilter && self.query && visibleCards === 0);
        cat.hidden = !inFilter || (self.query !== '' && visibleCards === 0);
      });
    }

    fadeIn(cat) {
      cat.classList.remove('iva-cat-in');
      void cat.offsetWidth;
      cat.classList.add('iva-cat-in');
    }

    initFilters() {
      if (!this.pills.length) return;
      var self = this;
      this.pills.forEach(function (p) {
        p.addEventListener('click', function () {
          self.activeFilter = p.getAttribute('data-iva-filter');
          self.pills.forEach(function (x) {
            var on = x === p;
            x.classList.toggle('is-active', on);
            x.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          self.render();
          self.cats.forEach(function (c) { if (!c.hidden) self.fadeIn(c); });
          self.scrollToResults();
        });
      });
    }

    // gently bring the filtered products into view (clears the sticky pills via
    // scroll-margin-top); instant for reduced-motion users
    scrollToResults() {
      var first = null;
      for (var i = 0; i < this.cats.length; i++) { if (!this.cats[i].hidden) { first = this.cats[i]; break; } }
      if (!first) return;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      first.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }

    initSearch() {
      var input = this.querySelector('[data-iva-search]');
      if (!input) return;
      var status = this.querySelector('[data-iva-status]');
      var word = input.getAttribute('data-results-word') || '';
      var self = this;
      var run = debounce(function () {
        self.query = input.value.trim().toLowerCase();
        self.render();
        var total = 0;
        self.cats.forEach(function (cat) {
          if (cat.hidden) return;
          toArr(cat.querySelectorAll('[data-iva-card]')).forEach(function (card) { if (!card.hidden) total++; });
        });
        if (status) status.textContent = self.query ? (total + ' ' + word) : '';
      }, 150);
      input.addEventListener('input', run);
    }

    initStickyBar() {
      var nav = this.querySelector('.iva-camp__pills--sticky');
      if (!nav) return;
      // Robust "stuck" detection: the bar is position:sticky/top:0, so it's pinned when
      // its top reaches ~0. A scroll/resize check on getBoundingClientRect().top is
      // reliable everywhere (incl. the Theme Editor iframe) — unlike the previous
      // IntersectionObserver-on-sentinel approach, which didn't reliably toggle is-stuck
      // (so the sticky logo, gated on .is-stuck, never appeared).
      var update = function () {
        nav.classList.toggle('is-stuck', nav.getBoundingClientRect().top <= 1);
      };
      this._stuckHandler = update;
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    }
  }

  customElements.define('iva-campaign-catalog', IvaCampaignCatalog);
})();
